/**
 * Enum for type of car body
 * @enum {string}
 * @property {string} SEDAN - Sedan car body type
 * @property {string} COUPE - Coupe car body type
 * @property {string} PICKUP - Pickup car body type
 * @property {string} SUV - SUV car body type
 */
export enum TypeCarBody {
  SEDAN = 'SEDAN',
  COUPE = 'COUPE',
  PICKUP = 'PICKUP',
  SUV = 'SUV',
}

/**
 * Enum for status
 * @readonly
 * @enum {string}
 * @property {string} ACTIVE - Active status
 * @property {string} BLOCKED - Blocked status
 * @property {string} DELETED - Deleted status
 */
export enum Status {
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  DELETED = 'DELETED',
}

/**
 * Enum for sort direction
 * @readonly
 * @enum {string}
 * @property {string} DESC - Sort down direction
 * @property {string} ASC - Sort up direction
 */
export enum SortDirectionType {
  DESC = 'desc',
  ASC = 'asc',
}

/**
 * Enum representing various permissions in the system.
 * Each permission corresponds to a specific operation that can be performed on a resource.
 *
 * @enum {string}
 * @property {string} ALA     - Alabama state.
 * @property {string} ALASKA  - Alaska state.
 * @property {string} ARIZ    - Arizona state.
 * @property {string} ARK     - Arkansas state.
 * @property {string} CALIF   - California state.
 * @property {string} CZ      - Canal Zone state.
 * @property {string} COLO    - Colorado state.
 * @property {string} CONN    - Connecticut state.
 * @property {string} DEL     - Delaware state.
 * @property {string} DC      - District of Columbia state.
 * @property {string} FLA     - Florida state.
 * @property {string} GA      - Georgia state.
 * @property {string} GUAM    - Guam state.
 * @property {string} HAWAII  - Hawaii state.
 * @property {string} IDAHO   - Idaho state.
 * @property {string} III     - Illinois state.
 * @property {string} IND     - Indiana state.
 * @property {string} IOWA    - Iowa state.
 * @property {string} KAN     - Kansas state.
 * @property {string} KY      - Kentucky state.
 * @property {string} LA      - Louisiana state.
 * @property {string} MAINE   - Maine state.
 * @property {string} MD      - Maryland state.
 * @property {string} MASS    - Massachusetts state.
 * @property {string} MICH    - Michigan state.
 * @property {string} MINN    - Minnesota state.
 * @property {string} MISS    - Mississippi state.
 * @property {string} MO      - Missouri state.
 * @property {string} MONT    - Montana state.
 * @property {string} NEB     - Nebraska state.
 * @property {string} NEV     - Nevada state.
 * @property {string} NH      - New Hampshire state.
 * @property {string} NJ      - New Jersey state.
 * @property {string} NM      - New Mexico state.
 * @property {string} NY      - New York state.
 * @property {string} NC      - North Carolina state.
 * @property {string} ND      - North Dakota state.
 * @property {string} OHIO    - Ohio state.
 * @property {string} OKLA    - Oklahoma state.
 * @property {string} ORE     - Oregon state.
 * @property {string} PA      - Pennsylvania state.
 * @property {string} PR      - Puerto Rico state.
 * @property {string} RI      - Rhode Island state.
 * @property {string} SC      - South Carolina state.
 * @property {string} SD      - South Dakota state.
 * @property {string} TENN    - Tennessee state.
 * @property {string} TEXAS   - Texas state.
 * @property {string} UTAH    - Utah state.,
 * @property {string} VT      - Vermont state.
 * @property {string} VI      - Virgin Islands state.
 * @property {string} VA      - Virginia state.
 * @property {string} WASH    - Washington state.
 * @property {string} WVA     - West Virginia state.
 * @property {string} WIS     - Wisconsin state.
 * @property {string} WYO     - Wyoming state.
 */
export enum State {
  ALA = 'AL',
  ALASKA = 'AK',
  ARIZ = 'AZ',
  ARK = 'AR',
  CALIF = 'CA',
  CZ = 'CZ',
  COLO = 'CO',
  CONN = 'CT',
  DEL = 'DE',
  DC = 'DC',
  FLA = 'FL',
  GA = 'GA',
  GUAM = 'GU',
  HAWAII = 'HI',
  IDAHO = 'ID',
  III = 'IL',
  IND = 'IN',
  IOWA = 'IA',
  KAN = 'KS',
  KY = 'KY',
  LA = 'LA',
  MAINE = 'ME',
  MD = 'MD',
  MASS = 'MA',
  MICH = 'MI',
  MINN = 'MN',
  MISS = 'MS',
  MO = 'MO',
  MONT = 'MT',
  NEB = 'NE',
  NEV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  OHIO = 'OH',
  OKLA = 'OK',
  ORE = 'OR',
  PA = 'PA',
  PR = 'PR',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TENN = 'TN',
  TEXAS = 'TX',
  UTAH = 'UT',
  VT = 'VT',
  VI = 'VI',
  VA = 'VA',
  WASH = 'WA',
  WVA = 'WV',
  WIS = 'WI',
  WYO = 'WY',
}

/**
 * Enum for Report Options Setting
 * @enum {string}
 * @property {string} ADJUSTED_VALUE_CAR - Is show adjusted value car in report
 * @property {string} INSPECTION_FLOW_QUESTIONS - Is show inspection flow questions in report
 * @property {string} ESTIMATE - Is show estimate info in report
 * @property {string} VIEW_IMAGES_360 - Is show 360 show frames info in report
 * @property {string} CONDITION_REPORT - Is show condition-report info in report
 * @property {string} TIRES - Is show tires section in report
 * @property {string} NWT_DAMAGES - Is show nwt damages section in leasing report
 * @property {string} DAMAGE_SOURCE - Is show damage source section in leasing report
 * @property {string} INSPECTOR_NOTES - Is show inspector notes in report
 */
export enum ReportOptions {
  ADJUSTED_VALUE_CAR = 'AVC',
  INSPECTION_FLOW_QUESTIONS = 'IFQ',
  ESTIMATE = 'EST',
  VIEW_IMAGES_360 = '3VI',
  CONDITION_REPORT = 'CR',
  TIRES = 'TIR',
  NWT_DAMAGES = 'NWD',
  DAMAGE_SOURCE = 'DSR',
  INSPECTOR_NOTES = 'IN',
}

/**
 * Enum for report actions with short codes.
 * @enum {string}
 * @property {string} SEND_REDO_STEPS - Send redo steps request
 * @property {string} LEASING_SEND_FOR_CORRECTIONS - Send leasing report for corrections
 * @property {string} LEASING_APPROVE - Approve leasing report
 * @property {string} LEASING_TOGGLE_CLAIM_DAMAGE - Toggle claim damage status
 * @property {string} LEASING_UPDATE_INITIALS - Update initials in leasing report
 * @property {string} LEASING_UPDATE_WAIVED - Update waived status in leasing report
 * @property {string} MANUAL_REQUIRE_ATTENTION - Manual require attention
 */
export enum ReportActions {
  SEND_REDO_STEPS = 'SRC',
  LEASING_SEND_FOR_CORRECTIONS = 'LSC',
  LEASING_APPROVE = 'LAP',
  LEASING_TOGGLE_CLAIM_DAMAGE = 'LTC',
  LEASING_UPDATE_INITIALS = 'LUI',
  LEASING_UPDATE_WAIVED = 'LUW',
  MANUAL_REQUIRE_ATTENTION = 'MRA',
}

/**
 * Enum for priority assessment
 * @enum {string}
 * @property {string} URGENT - Urgent priority
 * @property {string} MEDIUM - Medium priority
 * @property {string} LOW - Low priority
 */
export enum PriorityAssessment {
  URGENT = 'URGENT',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

/**
 * Enum for Vehicle Assessment Service
 * @enum {string}
 * @property {string} CR - Vehicle Condition Report
 * @property {string} EST - Estimation
 * @property {string} OBD - On-Board Diagnostics (mechanical diagnostics tool)
 * @property {string} AI - Auto Inspection
 * @property {string} AI - Auto Inspection wheel
 */
export enum VehicleAssessmentService {
  CR = 'CR',
  OBD = 'OBD',
  EST = 'EST',
  AI = 'AI',
  AIW = 'AIW',
}

/**
 * Enum for external and internal type
 * @enum {string}
 * @property {string} RGH - Rough condition
 * @property {string} AVG - Average condition
 * @property {string} CLN - Clean condition
 * @property {string} XCLN - Extra clean condition
 */
export enum ExternalAndInternalType {
  RGH = 'RGH',
  AVG = 'AVG',
  CLN = 'CLN',
  XCLN = 'XCLN',
}

/**
 * Enum for serving status
 * @enum {number}
 * @property {number} UNKNOWN - Unknown serving status
 * @property {number} SERVING - Currently serving
 * @property {number} NOT_SERVING - Not serving
 * @property {number} SERVICE_UNKNOWN - Service status unknown
 */
export enum ServingStatus {
  UNKNOWN,
  SERVING,
  NOT_SERVING,
  SERVICE_UNKNOWN,
}

/**
 * Enum for processed event sort fields
 * @enum {string}
 * @property {string} id - Sort by ID
 * @property {string} eventId - Sort by event ID
 * @property {string} createdAt - Sort by creation date
 */
export enum ProcessedEventSortField {
  id = 'id',
  eventId = 'eventId',
  createdAt = 'createdAt',
}

/**
 * Enumeration of supported currencies. The currency used on a report/estimate is derived from the
 * company location's country (see the currency symbol map in `locationFormating.helper.ts`).
 * @enum {string}
 * @property {string} USD - United States Dollar ($)
 * @property {string} AUD - Australian Dollar ($)
 * @property {string} EUR - Euro (€)
 * @property {string} GBP - British Pound (£)
 * @property {string} JPY - Japanese Yen (¥)
 * @property {string} CNY - Chinese Yuan (¥)
 * @property {string} BRL - Brazilian Real (R$)
 *
 * @example
 * const priceCurrency = Currency.USD;
 */
export enum Currency {
  USD = 'USD',
  AUD = 'AUD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  CNY = 'CNY',
  BRL = 'BRL',
}

/**
 * Enumeration of measurement systems.
 * @enum {string}
 * @property {string} METRIC - Metric system (e.g., meters, liters)
 * @property {string} IMPERIAL - Imperial system (e.g., feet, gallons)
 *
 * @example
 * const system = MeasurementSystem.METRIC;
 */
export enum MeasurementSystem {
  METRIC = 'METRIC',
  IMPERIAL = 'IMPERIAL',
}

/**
 * Enumeration of time formats.
 * @enum {string}
 * @property {string} HOUR_12 - 12-hour format (e.g., 01:00 PM)
 * @property {string} HOUR_24 - 24-hour format (e.g., 13:00)
 *
 * @example
 * const timeFormat = TimeFormat.HOUR_24;
 */
export enum TimeFormat {
  HOUR_12 = 'HOUR_12',
  HOUR_24 = 'HOUR_24',
}

/**
 * Enumeration of regional languages.
 * @enum {string}
 * @property {string} EN - English language
 * @property {string} ES - Spanish language
 * @property {string} DE - German language
 *
 * @example
 * const userLanguage = RegionalLanguage.EN;
 */
export enum RegionalLanguage {
  EN = 'en',
  ES = 'es',
  DE = 'de',
}

/**
 * Result of geocoding a location's address to coordinates.
 * @enum {string}
 * @property {string} PENDING - Not geocoded yet.
 * @property {string} OK - Resolved to a confident match.
 * @property {string} APPROX - Resolved, but to an approximate / low-confidence match.
 * @property {string} FAILED - The address could not be resolved to coordinates.
 */
export enum GeocodeStatus {
  PENDING = 'PENDING',
  OK = 'OK',
  APPROX = 'APPROX',
  FAILED = 'FAILED',
}
